import React, {
  Dispatch, ReactElement, SetStateAction, useState,
} from 'react';
import {
  Button,
  Checkbox, CheckboxGroup,
  Col,
  DatePicker,
  FormGroup,
  Heading,
  Input,
  LoaderButton,
  Radio,
  RadioGroup,
  Row,
  Table,
  Tbody,
  Td,
  Text, Th,
  Tr,
} from 'idsk-react-components';
import { useNavigate, useParams } from 'react-router';
import { ConfirmModal, TextEditor, Tooltip } from 'slovlex-idsk-react-components';
import { DolozkaVplyvovFormData } from '../../../../model/sprievodnyDokument/DolozkaVplyvovFormData';
import './dolozkaVplyvov.scss';
import { useClient } from '../../../../context/ClientStatusContext';
import LegislativnyMaterialDetail
  from '../../../../model/legislativnyMaterial/LegislativnyMaterialDetail';
import { tooltip5, tooltip6, tooltip7 } from '../../../../constants/Tooltips';
import routes from '../../../../routing/routes';
import { useSuccessMessageContext } from '../../../../context/SuccessMessageContext';
import { ClientResponse } from '../../../../model/ClientResponse';
import Uuid from '../../../../model/Uuid';
import useFieldErrorMessages from '../../../useFieldErrorMessages';
import FormCheckBox from '../component/FormCheckBox';
import { pouzivatelIsAdmin } from '../../../../function/permissions';
import { useUserContext } from '../../../../context/UserContext';

export interface PointProps {
  formData: DolozkaVplyvovFormData;
  setFormData: Dispatch<SetStateAction<DolozkaVplyvovFormData>>;
}

export interface PointPropsWithInitData extends PointProps {
  initFormData: DolozkaVplyvovFormData;
}

export const Point1 = ({ initFormData, formData, setFormData }: PointPropsWithInitData): ReactElement => {
  const { getFieldErrorMessage, clearFieldErrorMessage } = useFieldErrorMessages();
  return (
    <section className="dolozka-vplyvov-section">
      <Heading size="m">1. Základné údaje</Heading>
      <Text type="span" bold>Charakter predkladaného materiálu</Text>
      <Table>
        <Tbody>
          <Tr>
            <Td>
              <FormCheckBox
                toLeft
                formData={formData}
                setFormData={setFormData}
                prop="charakterMaterialuNelegislativny"
                label="Materiál nelegislatívnej povahy"
              />
            </Td>
          </Tr>
          <Tr>
            <Td><FormCheckBox
              toLeft
              formData={formData}
              setFormData={setFormData}
              prop="charakterMaterialuLegislativny"
              label="Materiál legislatívnej povahy"
            />
            </Td>
          </Tr>
          <Tr>
            <Td><FormCheckBox
              formData={formData}
              setFormData={setFormData}
              prop="charakterMaterialuTranspozicia"
              toLeft
              label="Transpozícia/implementácia práva EÚ"
            />
            </Td>
          </Tr>
        </Tbody>
      </Table>
      <FormGroup>
        <Row>
          <Col>
            <Text className="dolozka-vplyvov-label">
              V prípade transpozície/implementácie uveďte zoznam transponovaných/implementovaných predpisov:
            </Text>
            <TextEditor
              initialState={initFormData.zoznamTransponovanychPredpisov}
              onChange={(data: string) => setFormData({ ...formData, zoznamTransponovanychPredpisov: data })}
              maxLength={2048}
              showRemaining
            />
          </Col>
        </Row>
      </FormGroup>
      <Table>
        <Tbody>
          <Tr>
            <Td width="50%">Termín začiatku a ukončenia PPK</Td>
            <Td width="25%">
              <DatePicker
                showIcon
                id="terminZaciatkuPpk"
                width="full"
                value={formData.terminZaciatkuPpk}
                onChange={(date) => {
                  setFormData({
                    ...formData,
                    terminZaciatkuPpk: date,
                  });
                  clearFieldErrorMessage('terminZaciatkuPpk');
                }}
                error={getFieldErrorMessage('terminZaciatkuPpk')}
              />
            </Td>
            <Td width="25%">
              <DatePicker
                classNameWrapper="termin-ukoncenia-ppk date-picker-mobile"
                showIcon
                id="terminUkonceniaPpk"
                width="full"
                value={formData.terminUkonceniaPpk}
                onChange={(date) => {
                  setFormData({
                    ...formData,
                    terminUkonceniaPpk: date,
                  });
                  clearFieldErrorMessage('terminZaciatkuPpk');
                }}
                error={getFieldErrorMessage('terminZaciatkuPpk')}
              />
            </Td>
          </Tr>
          <Tr>
            <Td>Predpokladaný termín predloženia na pripomienkové konanie</Td>
            <Td>
              <Input
                type="text"
                value={formData.predpokladanyTerminPk}
                onChange={e => setFormData({
                  ...formData,
                  predpokladanyTerminPk: e.target.value,
                })}
                maxLength={64}
              />
            </Td>
            <Td />
          </Tr>
          <Tr>
            <Td>
              <Tooltip tooltipText={tooltip5}>Predpokladaný termín začiatku a ukončenia ZP</Tooltip>
            </Td>
            <Td>
              <Input
                type="text"
                value={formData.predpokladanyTerminZp}
                onChange={e => setFormData({
                  ...formData,
                  predpokladanyTerminZp: e.target.value,
                })}
                maxLength={64}
              />
            </Td>
            <Td />
          </Tr>
          <Tr>
            <Td>
              <Tooltip tooltipText={tooltip6}>Predpokladaný termín predloženia na rokovanie vlády
                SR
              </Tooltip>
            </Td>
            <Td>
              <Input
                type="text"
                value={formData.predpokladanyTerminRokovanieVlady}
                onChange={e => setFormData({
                  ...formData,
                  predpokladanyTerminRokovanieVlady: e.target.value,
                })}
                maxLength={64}
              />
            </Td>
            <Td />
          </Tr>
        </Tbody>
      </Table>
    </section>
  );
};

export const Point2 = ({ initFormData, formData, setFormData }: PointPropsWithInitData): ReactElement => (
  <section className="dolozka-vplyvov-section">
    <Heading size="m">2. Definovanie problému</Heading>
    <FormGroup>
      <Row>
        <Col>
          <Text className="dolozka-vplyvov-label">
            Uveďte základné problémy, ktoré sú dôvodom vypracovania predkladaného materiálu
            (dôvody majú presne poukázať na problém, ktorý existuje a je nutné ho predloženým materiálom riešiť).
          </Text>
          <TextEditor
            initialState={initFormData.definovanieProblemu}
            onChange={(data: string) => setFormData({ ...formData, definovanieProblemu: data })}
            maxLength={2048}
            showRemaining
          />
        </Col>
      </Row>
    </FormGroup>
  </section>
);

export const Point3 = ({ initFormData, formData, setFormData }: PointPropsWithInitData): ReactElement => (
  <section className="dolozka-vplyvov-section">
    <Heading size="m">3. Ciele a výsledný stav</Heading>
    <FormGroup>
      <Row>
        <Col>
          <Text className="dolozka-vplyvov-label">
            Uveďte hlavné ciele predkladaného materiálu (aký výsledný stav má byť prijatím materiálu dosiahnutý,
            pričom dosiahnutý stav musí byť odlišný od stavu popísaného v bode 2. Definovanie problému).
          </Text>
          <TextEditor
            initialState={initFormData.cieleAVyslednyStav}
            onChange={(data: string) => setFormData({ ...formData, cieleAVyslednyStav: data })}
            maxLength={2048}
            showRemaining
          />
        </Col>
      </Row>
    </FormGroup>
  </section>
);

export const Point4 = ({ initFormData, formData, setFormData }: PointPropsWithInitData): ReactElement => (
  <section className="dolozka-vplyvov-section">
    <Heading size="m">4. Dotknuté subjekty</Heading>
    <FormGroup>
      <Row>
        <Col>
          <Text className="dolozka-vplyvov-label">
            Uveďte subjekty, ktorých sa zmeny predkladaného materiálu dotknú priamo aj nepriamo:
          </Text>
          <TextEditor
            initialState={initFormData.dotknuteSubjekty}
            onChange={(data: string) => setFormData({ ...formData, dotknuteSubjekty: data })}
            maxLength={2048}
            showRemaining
          />
        </Col>
      </Row>
    </FormGroup>
  </section>
);

export const Point5 = ({ initFormData, formData, setFormData }: PointPropsWithInitData): ReactElement => (
  <section className="dolozka-vplyvov-section">
    <Heading size="m">5. Alternatívne riešenia</Heading>
    <FormGroup>
      <Row>
        <Col>
          <Text className="dolozka-vplyvov-label">
            Aké alternatívne riešenia vedúce k stanovenému cieľu boli identifikované a
            posudzované pre riešenie definovaného problému?
            Nulový variant - uveďte dôsledky, ku ktorým by došlo v prípade nevykonania úprav v predkladanom materiáli a
            alternatívne riešenia/spôsoby dosiahnutia cieľov uvedených v bode 3.
          </Text>
          <TextEditor
            initialState={initFormData.alternativneRiesenia}
            onChange={(data: string) => setFormData({ ...formData, alternativneRiesenia: data })}
            maxLength={2048}
            showRemaining
          />
        </Col>
      </Row>
    </FormGroup>
  </section>
);

export const Point6 = ({ initFormData, formData, setFormData }: PointPropsWithInitData): ReactElement => (
  <section className="dolozka-vplyvov-section">
    <Heading size="m">6. Vykonávacie predpisy</Heading>
    <Row>
      <Col width="three-quarters">
        <Text className="dolozka-vplyvov__radio-buttons-label" type="span">
          Predpokladá sa prijatie/zmena vykonávacích predpisov?
        </Text>
      </Col>
      <Col width="one-quarter">
        <RadioGroup inline small>
          <Radio
            title="Áno"
            checked={formData.vykonavaciePredpisyAno === true}
            id="vykonavacie-predpisy-ano"
            value="true"
            name="vykonavaciePredpisyAno"
            onChange={() => {
              setFormData({ ...formData, vykonavaciePredpisyAno: true });
            }}
          />
          <Radio
            title="Nie"
            checked={formData.vykonavaciePredpisyAno === false}
            id="vykonavacie-predpisy-nie"
            value="false"
            name="vykonavaciePredpisyNie"
            onChange={() => {
              setFormData({ ...formData, vykonavaciePredpisyAno: false });
            }}
          />
        </RadioGroup>
      </Col>
    </Row>
    <FormGroup>
      <Row>
        <Col>
          <Text className="dolozka-vplyvov-label">
            Ak áno, uveďte, ktoré oblasti budú nimi upravené, resp. ktorých vykonávacích predpisov sa zmena dotkne:
          </Text>
          <TextEditor
            initialState={initFormData.vykonavaciePredpisyText}
            onChange={(data: string) => setFormData({ ...formData, vykonavaciePredpisyText: data })}
            maxLength={2048}
            showRemaining
          />
        </Col>
      </Row>
    </FormGroup>
  </section>
);

export const Point7 = ({ initFormData, formData, setFormData }: PointPropsWithInitData): ReactElement => (
  <section className="dolozka-vplyvov-section">
    <Heading size="m">7. Transpozícia/implementácia práva EÚ</Heading>
    <Row>
      <Col width="three-quarters">
        <Text className="dolozka-vplyvov__radio-buttons-label" type="span">
          Uveďte, či v predkladanom návrhu právneho predpisu dochádza ku goldplatingu podľa tabuľky zhody,
          resp. či ku goldplatingu dochádza pri implementácii práva EÚ.
        </Text>
      </Col>
      <Col width="one-quarter">
        <RadioGroup inline small>
          <Radio
            title="Áno"
            checked={formData.transpoziciaGoldplatingAno === true}
            id="transpoziciaGoldplatingAno"
            value="true"
            name="transpoziciaGoldplatingAno"
            onChange={() => {
              setFormData({ ...formData, transpoziciaGoldplatingAno: true });
            }}
          />
          <Radio
            title="Nie"
            checked={formData.transpoziciaGoldplatingAno === false}
            id="transpoziciaGoldplatingNie"
            value="false"
            name="transpoziciaGoldplatingNie"
            onChange={() => {
              setFormData({ ...formData, transpoziciaGoldplatingAno: false });
            }}
          />
        </RadioGroup>
      </Col>
    </Row>
    <FormGroup>
      <Row>
        <Col>
          <Text className="dolozka-vplyvov-label">
            Ak áno, uveďte, ktorých vplyvov podľa bodu 9 sa goldplating týka:
          </Text>
          <TextEditor
            initialState={initFormData.transpoziciaPravaEuText}
            onChange={(data: string) => setFormData({ ...formData, transpoziciaPravaEuText: data })}
            maxLength={2048}
            showRemaining
          />
        </Col>
      </Row>
    </FormGroup>
  </section>
);

export const Point8 = ({ initFormData, formData, setFormData }: PointPropsWithInitData): ReactElement => (
  <section className="dolozka-vplyvov-section">
    <Heading size="m">8. Preskúmanie účelnosti</Heading>
    <FormGroup>
      <Row>
        <Col>
          <Text className="dolozka-vplyvov-label">
            Uveďte termín, kedy by malo dôjsť k preskúmaniu účinnosti a účelnosti predkladaného materiálu.
            Uveďte kritériá, na základe ktorých bude preskúmanie vykonané.
          </Text>
          <TextEditor
            initialState={initFormData.preskumanieUcelnosti}
            onChange={(data: string) => setFormData({ ...formData, preskumanieUcelnosti: data })}
            maxLength={2048}
            showRemaining
          />
        </Col>
      </Row>
    </FormGroup>
  </section>
);

export const Point9 = ({ formData, setFormData }: PointProps): ReactElement => (
  <section className="dolozka-vplyvov-section checkbox-group-mobile">
    <Heading size="m">9. Vybrané vplyvy materiálu</Heading>
    <Table>
      <Tbody>
        <Tr>
          <Th>Vplyvy na rozpočet verejnej správy</Th>
          <Td>
            <CheckboxGroup className="dolozka-vplyvov__inline-checkbox-group" small>
              <Checkbox
                className="dolozka-vplyvov__point-9-checkbox"
                id="vplyvyRozpocetVerejnejSpravyPozitivne"
                checked={formData.vplyvyRozpocetVerejnejSpravyPozitivne}
                onChange={e => setFormData({
                  ...formData, vplyvyRozpocetVerejnejSpravyPozitivne: e.target.checked,
                })}
              > Pozitívne
              </Checkbox>
              <Checkbox
                className="dolozka-vplyvov__point-9-checkbox"
                id="vplyvyRozpocetVerejnejSpravyZiadne"
                checked={formData.vplyvyRozpocetVerejnejSpravyZiadne}
                onChange={e => setFormData({
                  ...formData, vplyvyRozpocetVerejnejSpravyZiadne: e.target.checked,
                })}
              >Žiadne
              </Checkbox>
              <Checkbox
                className="dolozka-vplyvov__point-9-checkbox"
                id="vplyvyRozpocetVerejnejSpravyNegativne"
                checked={formData.vplyvyRozpocetVerejnejSpravyNegativne}
                onChange={e => setFormData({
                  ...formData, vplyvyRozpocetVerejnejSpravyNegativne: e.target.checked,
                })}
              >Negatívne
              </Checkbox>
            </CheckboxGroup>
          </Td>
        </Tr>
        <Tr>
          <Td className="dolozka-vplyvu__podpolozka">
            z toho rozpočtovo zabezpečené vplyvy, v prípade identifikovaného negatívneho vplyvu
          </Td>
          <Td>
            <CheckboxGroup className="dolozka-vplyvov__inline-checkbox-group" small>
              <Checkbox
                className="dolozka-vplyvov__point-9-checkbox"
                id="vplyvyRozpocetVerejnejSpravyZabezpeceneAno"
                checked={formData.vplyvyRozpocetVerejnejSpravyZabezpeceneAno}
                onChange={e => setFormData({
                  ...formData, vplyvyRozpocetVerejnejSpravyZabezpeceneAno: e.target.checked,
                })}
              > Áno
              </Checkbox>
              <Checkbox
                className="dolozka-vplyvov__point-9-checkbox"
                id="vplyvyRozpocetVerejnejSpravyZabezpeceneNie"
                checked={formData.vplyvyRozpocetVerejnejSpravyZabezpeceneNie}
                onChange={e => setFormData({
                  ...formData, vplyvyRozpocetVerejnejSpravyZabezpeceneNie: e.target.checked,
                })}
              >Nie
              </Checkbox>
              <Checkbox
                className="dolozka-vplyvov__point-9-checkbox"
                id="vplyvyRozpocetVerejnejSpravyZabezpeceneCiastocne"
                checked={formData.vplyvyRozpocetVerejnejSpravyZabezpeceneCiastocne}
                onChange={e => setFormData({
                  ...formData, vplyvyRozpocetVerejnejSpravyZabezpeceneCiastocne: e.target.checked,
                })}
              >Čiastočne
              </Checkbox>
            </CheckboxGroup>
          </Td>
        </Tr>
        <Tr>
          <Td className="podpolozka__bold">
            v tom vplyvy na rozpočty obcí a vyšších územných celkov
          </Td>
          <Td>
            <CheckboxGroup className="dolozka-vplyvov__inline-checkbox-group" small>
              <Checkbox
                className="dolozka-vplyvov__point-9-checkbox"
                id="vplyvyRozpoctyObciAVucPozitivne"
                checked={formData.vplyvyRozpoctyObciAVucPozitivne}
                onChange={e => setFormData({
                  ...formData, vplyvyRozpoctyObciAVucPozitivne: e.target.checked,
                })}
              > Pozitívne
              </Checkbox>
              <Checkbox
                className="dolozka-vplyvov__point-9-checkbox"
                id="vplyvyRozpoctyObciAVucZiadne"
                checked={formData.vplyvyRozpoctyObciAVucZiadne}
                onChange={e => setFormData({
                  ...formData, vplyvyRozpoctyObciAVucZiadne: e.target.checked,
                })}
              >Žiadne
              </Checkbox>
              <Checkbox
                className="dolozka-vplyvov__point-9-checkbox"
                id="vplyvyRozpoctyObciAVucNegativne"
                checked={formData.vplyvyRozpoctyObciAVucNegativne}
                onChange={e => setFormData({
                  ...formData, vplyvyRozpoctyObciAVucNegativne: e.target.checked,
                })}
              >Negatívne
              </Checkbox>
            </CheckboxGroup>
          </Td>
        </Tr>
        <Tr>
          <Td className="dolozka-vplyvu__podpolozka">
            z toho rozpočtovo zabezpečené vplyvy, v prípade identifikovaného negatívneho vplyvu
          </Td>
          <Td>
            <CheckboxGroup className="dolozka-vplyvov__inline-checkbox-group" small>
              <Checkbox
                className="dolozka-vplyvov__point-9-checkbox"
                id="vplyvyRozpoctyObciAVucZabezpeceneAno"
                checked={formData.vplyvyRozpoctyObciAVucZabezpeceneAno}
                onChange={e => setFormData({
                  ...formData, vplyvyRozpoctyObciAVucZabezpeceneAno: e.target.checked,
                })}
              > Áno
              </Checkbox>
              <Checkbox
                className="dolozka-vplyvov__point-9-checkbox"
                id="vplyvyRozpoctyObciAVucZabezpeceneNie"
                checked={formData.vplyvyRozpoctyObciAVucZabezpeceneNie}
                onChange={e => setFormData({
                  ...formData, vplyvyRozpoctyObciAVucZabezpeceneNie: e.target.checked,
                })}
              >Nie
              </Checkbox>
              <Checkbox
                className="dolozka-vplyvov__point-9-checkbox"
                id="vplyvyRozpoctyObciAVucZabezpeceneCiastocne"
                checked={formData.vplyvyRozpoctyObciAVucZabezpeceneCiastocne}
                onChange={e => setFormData({
                  ...formData, vplyvyRozpoctyObciAVucZabezpeceneCiastocne: e.target.checked,
                })}
              >Čiastočne
              </Checkbox>
            </CheckboxGroup>
          </Td>
        </Tr>
        <Tr>
          <Th className="dolozka-vplyvov__th-not-bold dolozka-vplyvu__podpolozka">
            <Tooltip tooltipText={tooltip7}>
              Vplyv na dlhodobú udržateľnosť verejných financií v prípade vybraných opatrení
            </Tooltip>
          </Th>
          <Td>
            <CheckboxGroup className="dolozka-vplyvov__inline-checkbox-group" small>
              <Checkbox
                className="dolozka-vplyvov__point-9-checkbox"
                id="vplyvNaUdrzatelnostAno"
                checked={formData.vplyvNaUdrzatelnostAno}
                onChange={e => setFormData({
                  ...formData, vplyvNaUdrzatelnostAno: e.target.checked,
                })}
              > Áno
              </Checkbox>
              <Checkbox
                className="dolozka-vplyvov__point-9-checkbox"
                id="vplyvNaUdrzatelnostNie"
                checked={formData.vplyvNaUdrzatelnostNie}
                onChange={e => setFormData({
                  ...formData, vplyvNaUdrzatelnostNie: e.target.checked,
                })}
              >Nie
              </Checkbox>
            </CheckboxGroup>
          </Td>
        </Tr>
        <Tr>
          <Th>Vplyvy na limit verejných výdavkov</Th>
          <Td>
            <CheckboxGroup className="dolozka-vplyvov__inline-checkbox-group" small>
              <Checkbox
                className="dolozka-vplyvov__point-9-checkbox"
                id="vplyvyNaLimitVydavkovPozitivne"
                checked={formData.vplyvyNaLimitVydavkovPozitivne}
                onChange={e => setFormData({
                  ...formData, vplyvyNaLimitVydavkovPozitivne: e.target.checked,
                })}
              > Pozitívne
              </Checkbox>
              <Checkbox
                className="dolozka-vplyvov__point-9-checkbox"
                id="vplyvyNaLimitVydavkovZiadne"
                checked={formData.vplyvyNaLimitVydavkovZiadne}
                onChange={e => setFormData({
                  ...formData, vplyvyNaLimitVydavkovZiadne: e.target.checked,
                })}
              >Žiadne
              </Checkbox>
              <Checkbox
                className="dolozka-vplyvov__point-9-checkbox"
                id="vplyvyNaLimitVydavkovNegativne"
                checked={formData.vplyvyNaLimitVydavkovNegativne}
                onChange={e => setFormData({
                  ...formData, vplyvyNaLimitVydavkovNegativne: e.target.checked,
                })}
              >Negatívne
              </Checkbox>
            </CheckboxGroup>
          </Td>
        </Tr>
        <Tr>
          <Th>Vplyvy na podnikateľské prostredie</Th>
          <Td>
            <CheckboxGroup className="dolozka-vplyvov__inline-checkbox-group" small>
              <Checkbox
                className="dolozka-vplyvov__point-9-checkbox"
                id="vplyvyPodnikatelskeProstrediePozitivne"
                checked={formData.vplyvyPodnikatelskeProstrediePozitivne}
                onChange={e => setFormData({
                  ...formData, vplyvyPodnikatelskeProstrediePozitivne: e.target.checked,
                })}
              > Pozitívne
              </Checkbox>
              <Checkbox
                className="dolozka-vplyvov__point-9-checkbox"
                id="vplyvyPodnikatelskeProstredieZiadne"
                checked={formData.vplyvyPodnikatelskeProstredieZiadne}
                onChange={e => setFormData({
                  ...formData, vplyvyPodnikatelskeProstredieZiadne: e.target.checked,
                })}
              >Žiadne
              </Checkbox>
              <Checkbox
                className="dolozka-vplyvov__point-9-checkbox"
                id="vplyvyPodnikatelskeProstredieNegativne"
                checked={formData.vplyvyPodnikatelskeProstredieNegativne}
                onChange={e => setFormData({
                  ...formData, vplyvyPodnikatelskeProstredieNegativne: e.target.checked,
                })}
              >Negatívne
              </Checkbox>
            </CheckboxGroup>
          </Td>
        </Tr>
        <Tr>
          <Td className="dolozka-vplyvu__podpolozka">z toho vplyvy na MSP</Td>
          <Td>
            <CheckboxGroup className="dolozka-vplyvov__inline-checkbox-group" small>
              <Checkbox
                className="dolozka-vplyvov__point-9-checkbox"
                id="vplyvyPodnikatelskeProstredieMspPozitivne"
                checked={formData.vplyvyPodnikatelskeProstredieMspPozitivne}
                onChange={e => setFormData({
                  ...formData, vplyvyPodnikatelskeProstredieMspPozitivne: e.target.checked,
                })}
              > Pozitívne
              </Checkbox>
              <Checkbox
                className="dolozka-vplyvov__point-9-checkbox"
                id="vplyvyPodnikatelskeProstredieMspZiadne"
                checked={formData.vplyvyPodnikatelskeProstredieMspZiadne}
                onChange={e => setFormData({
                  ...formData, vplyvyPodnikatelskeProstredieMspZiadne: e.target.checked,
                })}
              >Žiadne
              </Checkbox>
              <Checkbox
                className="dolozka-vplyvov__point-9-checkbox"
                id="vplyvyPodnikatelskeProstredieMspNegativne"
                checked={formData.vplyvyPodnikatelskeProstredieMspNegativne}
                onChange={e => setFormData({
                  ...formData, vplyvyPodnikatelskeProstredieMspNegativne: e.target.checked,
                })}
              >Negatívne
              </Checkbox>
            </CheckboxGroup>
          </Td>
        </Tr>
        <Tr>
          <Td className="dolozka-vplyvu__podpolozka">Mechanizmus znižovania byrokracie a nákladov sa uplatňuje</Td>
          <Td>
            <CheckboxGroup className="dolozka-vplyvov__inline-checkbox-group" small>
              <Checkbox
                className="dolozka-vplyvov__point-9-checkbox"
                id="vplyvyPodnikatelskeProstredieZnizovanieByrokracieAno"
                checked={formData.vplyvyPodnikatelskeProstredieZnizovanieByrokracieAno}
                onChange={e => setFormData({
                  ...formData, vplyvyPodnikatelskeProstredieZnizovanieByrokracieAno: e.target.checked,
                })}
              > Áno
              </Checkbox>
              <Checkbox
                className="dolozka-vplyvov__point-9-checkbox"
                id="vplyvyPodnikatelskeProstredieZnizovanieByrokracieNie"
                checked={formData.vplyvyPodnikatelskeProstredieZnizovanieByrokracieNie}
                onChange={e => setFormData({
                  ...formData, vplyvyPodnikatelskeProstredieZnizovanieByrokracieNie: e.target.checked,
                })}
              >Nie
              </Checkbox>
            </CheckboxGroup>
          </Td>
        </Tr>
        <Tr>
          <Th>Sociálne vplyvy</Th>
          <Td>
            <CheckboxGroup className="dolozka-vplyvov__inline-checkbox-group" small>
              <Checkbox
                className="dolozka-vplyvov__point-9-checkbox"
                id="socialneVplyvyPozitivne"
                checked={formData.socialneVplyvyPozitivne}
                onChange={e => setFormData({
                  ...formData, socialneVplyvyPozitivne: e.target.checked,
                })}
              > Pozitívne
              </Checkbox>
              <Checkbox
                className="dolozka-vplyvov__point-9-checkbox"
                id="socialneVplyvyZiadne"
                checked={formData.socialneVplyvyZiadne}
                onChange={e => setFormData({
                  ...formData, socialneVplyvyZiadne: e.target.checked,
                })}
              >Žiadne
              </Checkbox>
              <Checkbox
                className="dolozka-vplyvov__point-9-checkbox"
                id="socialneVplyvyNegativne"
                checked={formData.socialneVplyvyNegativne}
                onChange={e => setFormData({
                  ...formData, socialneVplyvyNegativne: e.target.checked,
                })}
              >Negatívne
              </Checkbox>
            </CheckboxGroup>
          </Td>
        </Tr>
        <Tr>
          <Th>Vplyvy na životné prostredie</Th>
          <Td>
            <CheckboxGroup className="dolozka-vplyvov__inline-checkbox-group" small>
              <Checkbox
                className="dolozka-vplyvov__point-9-checkbox"
                id="vplyvyZivotneProstrediePozitivne"
                checked={formData.vplyvyZivotneProstrediePozitivne}
                onChange={e => setFormData({
                  ...formData, vplyvyZivotneProstrediePozitivne: e.target.checked,
                })}
              > Pozitívne
              </Checkbox>
              <Checkbox
                className="dolozka-vplyvov__point-9-checkbox"
                id="vplyvyZivotneProstredieZiadne"
                checked={formData.vplyvyZivotneProstredieZiadne}
                onChange={e => setFormData({
                  ...formData, vplyvyZivotneProstredieZiadne: e.target.checked,
                })}
              >Žiadne
              </Checkbox>
              <Checkbox
                className="dolozka-vplyvov__point-9-checkbox"
                id="vplyvyZivotneProstredieNegativne"
                checked={formData.vplyvyZivotneProstredieNegativne}
                onChange={e => setFormData({
                  ...formData, vplyvyZivotneProstredieNegativne: e.target.checked,
                })}
              >Negatívne
              </Checkbox>
            </CheckboxGroup>
          </Td>
        </Tr>
        <Tr>
          <Td className="dolozka-vplyvu__podpolozka">Materiál je posudzovaný podľa zákona č.
            24/2006 Z. z. o posudzovaní vplyvov na životné prostredie a o zmene a doplnení
            niektorých zákonov v znení neskorších predpisov
          </Td>
          <Td>
            <CheckboxGroup className="dolozka-vplyvov__inline-checkbox-group" small>
              <Checkbox
                className="dolozka-vplyvov__point-9-checkbox"
                id="posudzovanyPodlaZakonaAno"
                checked={formData.posudzovanyPodlaZakonaAno}
                onChange={e => setFormData({
                  ...formData, posudzovanyPodlaZakonaAno: e.target.checked,
                })}
              > Áno
              </Checkbox>
              <Checkbox
                className="dolozka-vplyvov__point-9-checkbox"
                id="posudzovanyPodlaZakonaNie"
                checked={formData.posudzovanyPodlaZakonaNie}
                onChange={e => setFormData({
                  ...formData, posudzovanyPodlaZakonaNie: e.target.checked,
                })}
              >Nie
              </Checkbox>
            </CheckboxGroup>
          </Td>
        </Tr>
        <Tr>
          <Th>Vplyvy na informatizáciu spoločnosti</Th>
          <Td>
            <CheckboxGroup className="dolozka-vplyvov__inline-checkbox-group" small>
              <Checkbox
                className="dolozka-vplyvov__point-9-checkbox"
                id="vplyvyInformatizaciaSpolocnostiPozitivne"
                checked={formData.vplyvyInformatizaciaSpolocnostiPozitivne}
                onChange={e => setFormData({
                  ...formData, vplyvyInformatizaciaSpolocnostiPozitivne: e.target.checked,
                })}
              > Pozitívne
              </Checkbox>
              <Checkbox
                className="dolozka-vplyvov__point-9-checkbox"
                id="vplyvyInformatizaciaSpolocnostiZiadne"
                checked={formData.vplyvyInformatizaciaSpolocnostiZiadne}
                onChange={e => setFormData({
                  ...formData, vplyvyInformatizaciaSpolocnostiZiadne: e.target.checked,
                })}
              >Žiadne
              </Checkbox>
              <Checkbox
                className="dolozka-vplyvov__point-9-checkbox"
                id="vplyvyInformatizaciaSpolocnostiNegativne"
                checked={formData.vplyvyInformatizaciaSpolocnostiNegativne}
                onChange={e => setFormData({
                  ...formData, vplyvyInformatizaciaSpolocnostiNegativne: e.target.checked,
                })}
              >Negatívne
              </Checkbox>
            </CheckboxGroup>
          </Td>
        </Tr>
        <Tr>
          <Th className="th-without-border">
            Vplyvy na služby verejnej správy pre občana, z toho
          </Th>
        </Tr>
        <Tr>
          <Td className="dolozka-vplyvu__podpolozka podpolozka__bold">vplyvy služieb verejnej správy na občana</Td>
          <Td>
            <CheckboxGroup className="dolozka-vplyvov__inline-checkbox-group" small>
              <Checkbox
                className="dolozka-vplyvov__point-9-checkbox"
                id="vplyvyVerejnaSpravaObcanPozitivne"
                checked={formData.vplyvyVerejnaSpravaObcanPozitivne}
                onChange={e => setFormData({
                  ...formData, vplyvyVerejnaSpravaObcanPozitivne: e.target.checked,
                })}
              > Pozitívne
              </Checkbox>
              <Checkbox
                className="dolozka-vplyvov__point-9-checkbox"
                id="vplyvyVerejnaSpravaObcanZiadne"
                checked={formData.vplyvyVerejnaSpravaObcanZiadne}
                onChange={e => setFormData({
                  ...formData, vplyvyVerejnaSpravaObcanZiadne: e.target.checked,
                })}
              >Žiadne
              </Checkbox>
              <Checkbox
                className="dolozka-vplyvov__point-9-checkbox"
                id="vplyvyVerejnaSpravaObcanNegativne"
                checked={formData.vplyvyVerejnaSpravaObcanNegativne}
                onChange={e => setFormData({
                  ...formData, vplyvyVerejnaSpravaObcanNegativne: e.target.checked,
                })}
              >Negatívne
              </Checkbox>
            </CheckboxGroup>
          </Td>
        </Tr>
        <Tr>
          <Td className="dolozka-vplyvu__podpolozka podpolozka__bold">vplyvy na procesy služieb vo verejnej správe</Td>
          <Td>
            <CheckboxGroup className="dolozka-vplyvov__inline-checkbox-group" small>
              <Checkbox
                className="dolozka-vplyvov__point-9-checkbox"
                id="vplyvyProcesyVerejnejSpravyPozitivne"
                checked={formData.vplyvyProcesyVerejnejSpravyPozitivne}
                onChange={e => setFormData({
                  ...formData, vplyvyProcesyVerejnejSpravyPozitivne: e.target.checked,
                })}
              > Pozitívne
              </Checkbox>
              <Checkbox
                className="dolozka-vplyvov__point-9-checkbox"
                id="vplyvyProcesyVerejnejSpravyZiadne"
                checked={formData.vplyvyProcesyVerejnejSpravyZiadne}
                onChange={e => setFormData({
                  ...formData, vplyvyProcesyVerejnejSpravyZiadne: e.target.checked,
                })}
              >Žiadne
              </Checkbox>
              <Checkbox
                className="dolozka-vplyvov__point-9-checkbox"
                id="vplyvyProcesyVerejnejSpravyNegativne"
                checked={formData.vplyvyProcesyVerejnejSpravyNegativne}
                onChange={e => setFormData({
                  ...formData, vplyvyProcesyVerejnejSpravyNegativne: e.target.checked,
                })}
              >Negatívne
              </Checkbox>
            </CheckboxGroup>
          </Td>
        </Tr>
        <Tr>
          <Th>Vplyvy na manželstvo, rodičovstvo a rodinu</Th>
          <Td>
            <CheckboxGroup className="dolozka-vplyvov__inline-checkbox-group" small>
              <Checkbox
                className="dolozka-vplyvov__point-9-checkbox"
                id="vplyvyManzelstvoRodicovstvoPozitivne"
                checked={formData.vplyvyManzelstvoRodicovstvoPozitivne}
                onChange={e => setFormData({
                  ...formData, vplyvyManzelstvoRodicovstvoPozitivne: e.target.checked,
                })}
              > Pozitívne
              </Checkbox>
              <Checkbox
                className="dolozka-vplyvov__point-9-checkbox"
                id="vplyvyManzelstvoRodicovstvoZiadne"
                checked={formData.vplyvyManzelstvoRodicovstvoZiadne}
                onChange={e => setFormData({
                  ...formData, vplyvyManzelstvoRodicovstvoZiadne: e.target.checked,
                })}
              >Žiadne
              </Checkbox>
              <Checkbox
                className="dolozka-vplyvov__point-9-checkbox"
                id="vplyvyManzelstvoRodicovstvoNegativne"
                checked={formData.vplyvyManzelstvoRodicovstvoNegativne}
                onChange={e => setFormData({
                  ...formData, vplyvyManzelstvoRodicovstvoNegativne: e.target.checked,
                })}
              >Negatívne
              </Checkbox>
            </CheckboxGroup>
          </Td>
        </Tr>
      </Tbody>
    </Table>
  </section>
);

export const Point10 = ({ initFormData, formData, setFormData }: PointPropsWithInitData): ReactElement => (
  <section className="dolozka-vplyvov-section">
    <Heading size="m">10. Poznámky</Heading>
    <FormGroup>
      <Row>
        <Col>
          <Text className="dolozka-vplyvov-label">
            V prípade potreby uveďte doplňujúce informácie k identifikovaným vplyvom a ich analýzam.
            <br /><br />
            Ak  predkladaný  materiál  má  marginálny  (zanedbateľný)  vplyv  na  niektorú  zo  sledovaných
            oblastí  v bode  9 a z tohto dôvodu je tento vplyv označený ako žiadny vplyv, uveďte skutočnosti
            vysvetľujúce, prečo je tento vplyv marginálny (zanedbateľný).
            <br /><br />
            Informácie v tejto časti slúžia na zhrnutie vplyvov alebo aj na vyjadrenie sa k marginálnym vplyvom
            a nie ako náhrada za vypracovanie príslušných analýz vybraných vplyvov.
            <br /><br />
            V prípade, že je materiál posudzovaný podľa zákona č. 24/2006 Z. z. o posudzovaní vplyvov na životné
            prostredie a o zmene a doplnení niektorých zákonov v znení neskorších prepisov, uveďte
            internetový odkaz na tento proces.
          </Text>
          <TextEditor
            initialState={initFormData.poznamky}
            onChange={(data: string) => setFormData({ ...formData, poznamky: data })}
            maxLength={2048}
            showRemaining
          />
        </Col>
      </Row>
    </FormGroup>
  </section>
);

export const Point11 = ({ initFormData, formData, setFormData }: PointPropsWithInitData): ReactElement => (
  <section className="dolozka-vplyvov-section">
    <Heading size="m">11. Kontakt na spracovateľa</Heading>
    <FormGroup>
      <Row>
        <Col>
          <Text className="dolozka-vplyvov-label">
            Uveďte údaje na kontaktnú osobu, ktorú je možné kontaktovať v súvislosti s posúdením vybraných vplyvov.
            Odporúča sa uviesť meno, priezvisko a funkciu spracovateľa, e-mailový a telefonický kontakt.
          </Text>
          <TextEditor
            initialState={initFormData.kontaktSpracovatel}
            onChange={(data: string) => setFormData({ ...formData, kontaktSpracovatel: data })}
            maxLength={2048}
            showRemaining
          />
        </Col>
      </Row>
    </FormGroup>
  </section>
);

export const Point12 = ({ initFormData, formData, setFormData }: PointPropsWithInitData): ReactElement => (
  <section className="dolozka-vplyvov-section">
    <Heading size="m">12. Zdroje</Heading>
    <FormGroup>
      <Row>
        <Col>
          <Text className="dolozka-vplyvov-label">
            Uveďte zdroje (štatistiky, prieskumy, spoluprácu s odborníkmi a iné), z ktorých ste pri príprave materiálu
            a vypracovávaní doložky, analýz vplyvov vychádzali. V prípade nedostupnosti potrebných dát pre spracovanie
            relevantných analýz vybraných vplyvov, uveďte danú skutočnosť.
          </Text>
          <TextEditor
            initialState={initFormData.zdroje}
            onChange={(data: string) => setFormData({ ...formData, zdroje: data })}
            maxLength={2048}
            showRemaining
          />
        </Col>
      </Row>
    </FormGroup>
  </section>
);

export const Point13 = ({ initFormData, formData, setFormData }: PointPropsWithInitData): ReactElement => (
  <section className="dolozka-vplyvov-section">
    <Heading className="dolozka-vplyvov__heading-with-input" size="m">
      13. Stanovisko Komisie na posudzovanie vybraných vplyvov z PPK č.
    </Heading>
    <Input
      width="one-third"
      type="text"
      value={formData.stanoviskoPpkC}
      onChange={e => setFormData({ ...formData, stanoviskoPpkC: e.target.value })}
      maxLength={256}
    />
    <Text type="p" className="heading-info">
      (v prípade, ak sa uskutočnilo v zmysle bodu 8.1 Jednotnej metodiky)
    </Text>
    <CheckboxGroup className="dolozka-vplyvov__inline-checkbox-group" small>
      <Checkbox
        className="dolozka-vplyvov__checkbox"
        id="stanoviskoPpkSuhlasne"
        checked={formData.stanoviskoPpkSuhlasne}
        onChange={e => setFormData({
          ...formData, stanoviskoPpkSuhlasne: e.target.checked,
        })}
      > Súhlasné
      </Checkbox>
      <Checkbox
        className="dolozka-vplyvov__checkbox"
        id="stanoviskoPpkSuhlasneNaDopracovanie"
        checked={formData.stanoviskoPpkSuhlasneNaDopracovanie}
        onChange={e => setFormData({
          ...formData, stanoviskoPpkSuhlasneNaDopracovanie: e.target.checked,
        })}
      >Súhlasné s návrhom na dopracovanie
      </Checkbox>
      <Checkbox
        className="dolozka-vplyvov__checkbox"
        id="stanoviskoPpkNesuhlasne"
        checked={formData.stanoviskoPpkNesuhlasne}
        onChange={e => setFormData({
          ...formData, stanoviskoPpkNesuhlasne: e.target.checked,
        })}
      >Nesúhlasné
      </Checkbox>
    </CheckboxGroup>
    <FormGroup className="dolozka-vplyvov__textarea">
      <Row>
        <Col>
          <Text className="dolozka-vplyvov-label">
            Uveďte pripomienky zo stanoviska Komisie z časti II. spolu s Vaším vyhodnotením:
          </Text>
          <TextEditor
            initialState={initFormData.stanoviskoPpkVyhodnotenie}
            onChange={(data: string) => setFormData({ ...formData, stanoviskoPpkVyhodnotenie: data })}
            maxLength={2048}
            showRemaining
          />
        </Col>
      </Row>
    </FormGroup>
  </section>
);

export const Point14 = ({ initFormData, formData, setFormData }: PointPropsWithInitData): ReactElement => (
  <section className="dolozka-vplyvov-section">
    <Heading className="dolozka-vplyvov__heading-with-input" size="m">
      14. Stanovisko Komisie na posudzovanie vybraných vplyvov zo záverečného posúdenia č.
    </Heading>
    <Input
      width="one-third"
      type="text"
      value={formData.stanoviskoPosudenieC}
      onChange={e => setFormData({ ...formData, stanoviskoPosudenieC: e.target.value })}
      maxLength={256}
    />
    <Text type="p" className="heading-info">
      (v prípade, ak sa uskutočnilo v zmysle bodu 9.1 Jednotnej metodiky)
    </Text>
    <CheckboxGroup className="dolozka-vplyvov__inline-checkbox-group" small>
      <Checkbox
        className="dolozka-vplyvov__checkbox"
        id="stanoviskoPosudenieSuhlasne"
        checked={formData.stanoviskoPosudenieSuhlasne}
        onChange={e => setFormData({
          ...formData, stanoviskoPosudenieSuhlasne: e.target.checked,
        })}
      > Súhlasné
      </Checkbox>
      <Checkbox
        className="dolozka-vplyvov__checkbox"
        id="stanoviskoPosudenieSuhlasneNaDopracovanie"
        checked={formData.stanoviskoPosudenieSuhlasneNaDopracovanie}
        onChange={e => setFormData({
          ...formData, stanoviskoPosudenieSuhlasneNaDopracovanie: e.target.checked,
        })}
      >Súhlasné s návrhom na dopracovanie
      </Checkbox>
      <Checkbox
        className="dolozka-vplyvov__checkbox"
        id="stanoviskoPosudenieNesuhlasne"
        checked={formData.stanoviskoPosudenieNesuhlasne}
        onChange={e => setFormData({
          ...formData, stanoviskoPosudenieNesuhlasne: e.target.checked,
        })}
      >Nesúhlasné
      </Checkbox>
    </CheckboxGroup>
    <FormGroup className="dolozka-vplyvov__textarea">
      <Row>
        <Col>
          <Text className="dolozka-vplyvov-label">
            Uveďte pripomienky zo stanoviska Komisie z časti II. spolu s Vaším vyhodnotením:
          </Text>
          <TextEditor
            initialState={initFormData.stanoviskoPosudenieVyhodnotenie}
            onChange={(data: string) => setFormData({ ...formData, stanoviskoPosudenieVyhodnotenie: data })}
            maxLength={2048}
            showRemaining
          />
        </Col>
      </Row>
    </FormGroup>
  </section>
);

interface DolozkaVplyvovProps {
  onSubmit: (uuid: Uuid, form: DolozkaVplyvovFormData) => Promise<ClientResponse<void>>;
  material: LegislativnyMaterialDetail,
  initFormData: DolozkaVplyvovFormData,
}

const DolozkaVplyvov = ({
  onSubmit, material, initFormData,
}: DolozkaVplyvovProps): ReactElement => {
  const [formData, setFormData] = useState<DolozkaVplyvovFormData>(initFormData);
  const navigate = useNavigate();
  const { displaySuccess } = useSuccessMessageContext();
  const {
    type,
    year,
    index,
    stadiumUuid,
    // @ts-ignore
  } = useParams<{ type: string, year: number, index: number, stadiumUuid: string }>();
  const { pouzivatel } = useUserContext();

  const {
    wrapClient: submitDolozkaVplyvov,
    status,
  } = useClient(onSubmit);

  const onSubmitForm = async (): Promise<void> => {
    await submitDolozkaVplyvov(material.uuid, formData, stadiumUuid);
    navigate(
      pouzivatelIsAdmin(pouzivatel)
        ? routes.procesy.detail.sprievodneDokumenty.editAsAdmin.withParams(type, year, index, stadiumUuid)
        : routes.procesy.detail.sprievodneDokumenty.edit.withParams(type, year, index),
    );
    displaySuccess('Dokument bol úspešne uložený');
  };

  return (
    <div className="dolozka-vplyvov">
      <section className="dolozka-vplyvov__form">
        <Point1 initFormData={initFormData} formData={formData} setFormData={setFormData} />
        <Point2 initFormData={initFormData} formData={formData} setFormData={setFormData} />
        <Point3 initFormData={initFormData} formData={formData} setFormData={setFormData} />
        <Point4 initFormData={initFormData} formData={formData} setFormData={setFormData} />
        <Point5 initFormData={initFormData} formData={formData} setFormData={setFormData} />
        <Point6 initFormData={initFormData} formData={formData} setFormData={setFormData} />
        <Point7 initFormData={initFormData} formData={formData} setFormData={setFormData} />
        <Point8 initFormData={initFormData} formData={formData} setFormData={setFormData} />
        <Point9 formData={formData} setFormData={setFormData} />
        <Point10 initFormData={initFormData} formData={formData} setFormData={setFormData} />
        <Point11 initFormData={initFormData} formData={formData} setFormData={setFormData} />
        <Point12 initFormData={initFormData} formData={formData} setFormData={setFormData} />
        <Point13 initFormData={initFormData} formData={formData} setFormData={setFormData} />
        <Point14 initFormData={initFormData} formData={formData} setFormData={setFormData} />
        <FormGroup className="dolozka-vplyvov__form-buttons">
          <ConfirmModal
            question="Naozaj si prajete uložiť formulár a vytvoriť sprievodný dokument?"
            description={'Ak upravujete existujúci formulár, budú údaje tohto formulára prepísané '
              + 'a výsledný dokument, ktorý vznikol na základe tohto formulára, bude nahradený.'}
            cancelButtonText="Nie"
            confirmButton={
              (
                <Button
                  buttonStyle="action"
                  size="large"
                >
                  Áno
                </Button>
              )
            }
            openButton={(
              <LoaderButton
                loading={status.loading}
                type="submit"
                size="large"
                disableOutsideClickWhileLoading
              >Uložiť
              </LoaderButton>
            )}
            doAfterConfirm={() => onSubmitForm()}
          />
          <Button
            className="dolozka-vplyvov__buttons"
            type="button"
            buttonStyle="third"
            size="large"
            disabled={status.loading}
            onClick={() => navigate(-1)}
          >Späť
          </Button>
        </FormGroup>
      </section>
    </div>
  );
};

export default DolozkaVplyvov;
